<template>
  <div class="chat-page">
    <div class="chat-layout">
      <div class="user-panel">
        <div class="panel-title">在线咨询</div>
        <div class="user-list">
          <div
            class="user-item"
            v-for="u in users"
            :key="u.userId"
            :class="{ active: u.userId === activeUser }"
            @click="selectUser(u)"
          >
            <div class="avatar">{{ u.name.charAt(0) }}</div>
            <div class="user-info">
              <div class="user-name">{{ u.name }}</div>
              <div class="user-last">{{ u.lastMsg }}</div>
            </div>
            <div class="badge" v-if="u.unread">{{ u.unread }}</div>
          </div>
          <div v-if="users.length === 0" class="empty-hint">当前版本暂无咨询</div>
        </div>
      </div>

      <div class="chat-panel" v-if="activeUser">
        <div class="chat-header">
          <span>{{ activeUserName }}</span>
        </div>
        <div class="chat-body" ref="chatBody">
          <div
            class="msg"
            v-for="(m, i) in activeMessages"
            :key="i"
            :class="{ self: m.fromRole === 'admin' }"
          >
            <div class="bubble">{{ m.message }}</div>
            <div class="time">{{ formatTime(m.createdAt) }}</div>
          </div>
        </div>
        <div class="chat-footer">
          <input v-model="input" type="text" placeholder="输入回复..." @keyup.enter="send" />
          <button @click="send">发送</button>
        </div>
      </div>

      <div class="chat-panel empty" v-else>
        <div class="empty-hint">请选择一位用户开始对话</div>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '@/utils/api';
import { getAdminSite } from '@/utils/site';

export default {
  name: 'AdminChat',
  data() {
    return {
      activeUser: null,
      input: '',
      users: [],
      messages: {},
      timer: null
    };
  },
  computed: {
    activeUserName() {
      const u = this.users.find(u => u.userId === this.activeUser);
      return u ? u.name : '';
    },
    activeMessages() {
      return this.activeUser ? this.messages[this.activeUser] || [] : [];
    }
  },
  async created() {
    await this.fetchUsers();
    this.timer = setInterval(() => this.fetchUsers(), 5000);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  watch: {
    activeUser() {
      this.$nextTick(() => this.scrollBottom());
    }
  },
  methods: {
    currentSite() {
      return getAdminSite();
    },
    async fetchUsers() {
      const data = await api.get('/api/admin/chat/users?site=' + this.currentSite());
      if (data.code === 200) this.users = data.data.users || [];
    },
    async selectUser(u) {
      this.activeUser = u.userId;
      const data = await api.get('/api/admin/chat/messages/' + u.userId + '?site=' + this.currentSite());
      if (data.code === 200) {
        this.messages[u.userId] = data.data.messages || [];
        this.$nextTick(() => this.scrollBottom());
      }
    },
    async send() {
      const msg = this.input.trim();
      if (!msg || !this.activeUser) return;
      this.input = '';
      const res = await api.post('/api/admin/chat/messages/' + this.activeUser + '/send', { message: msg, site: this.currentSite() });
      if (res.code === 200) {
        const list = this.messages[this.activeUser] || [];
        list.push({ fromRole: 'admin', message: msg, createdAt: new Date().toISOString() });
        this.$nextTick(() => this.scrollBottom());
        this.fetchUsers();
      }
    },
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    },
    scrollBottom() {
      const el = this.$refs.chatBody;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }
};
</script>

<style scoped>
.chat-page {
  height: calc(100vh - 110px);
  padding: 15px;
  display: flex;
  flex-direction: column;
}
.chat-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  height: 100%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.user-panel {
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
}
.panel-title {
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
}
.user-list {
  flex: 1;
  overflow-y: auto;
}
.user-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.user-item:hover, .user-item.active {
  background: #f5faf7;
}
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #27ae60;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  flex-shrink: 0;
}
.user-info {
  flex: 1;
  min-width: 0;
}
.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}
.user-last {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.badge {
  background: #e74c3c;
  color: #fff;
  font-size: 11px;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.chat-panel.empty {
  align-items: center;
  justify-content: center;
}
.empty-hint {
  color: #ccc;
  font-size: 15px;
  padding: 20px;
}
.chat-header {
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.chat-body {
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
}
.msg {
  margin-bottom: 16px;
}
.msg.self {
  text-align: right;
}
.msg.self .bubble {
  background: #27ae60;
  color: #fff;
}
.bubble {
  display: inline-block;
  max-width: 80%;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;
  text-align: left;
  word-break: break-word;
}
.time {
  font-size: 11px;
  color: #ccc;
  margin-top: 4px;
}
.chat-footer {
  display: flex;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  gap: 10px;
  flex-shrink: 0;
}
.chat-footer input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
}
.chat-footer input:focus {
  border-color: #27ae60;
}
.chat-footer button {
  padding: 10px 22px;
  background: #27ae60;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}
</style>
